import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewEncapsulation,
} from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";

import {
  LocalizeService,
  AuthService,
  InsaService,
} from "../../insa-services";
import * as OrderLogic from "./control-table-order.logic";

import { ControlTableService } from "./control-table.service";
import { ControlTableSecurity } from "./control-table.models";

// Nalog s Liste za praćenje - provjere, upozorenja i izračuni usklađeni s
// traderom (pravila u control-table-order.logic.ts, preslikana iz tradera):
// unos (živi izračun naknada) -> potvrda (TraderOrderCheck) -> TraderOrderInsert.
@Component({
  selector: "app-control-table-order-modal",
  templateUrl: "./control-table-order-modal.component.html",
  styles: [require("./control-table.component.css").toString()],
  encapsulation: ViewEncapsulation.None,
})
export class ControlTableOrderModalComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() security: ControlTableSecurity | null = null;
  @Input() side: "Buy" | "Sell" = "Buy";
  // valuta likvidnosti (kao liquidityCurrency na traderu)
  @Input() currency = "";

  @Output() closed = new EventEmitter<void>();

  readonly gtdInputId = "ctGtdDate";

  isDomestic = true;
  allowedTransactionTypes: number[] = [];
  allowedOrderTypes: number[] = [];

  selectedOrderType = 2;
  selectedValidity = "Day";
  goodTillDate = "";

  tradeAmount = 0;
  tradePrice = 0;
  tradeStopPrice = 0;
  tradeTotal = 0;

  currentPrice = 0;
  availableLiquidity = 0;
  heldQuantity = 0;
  checkCashBalance = true;
  private secReservationPercent = 1;

  cashBalanceLoading = false;
  heldQuantityLoading = false;

  liveCheckAmounts: { [key: string]: number } | null = null;
  liveCheckLoading = false;

  // "form" = unos naloga, "confirm" = potvrda/rezultat (kao confirm modal na traderu)
  step: "form" | "confirm" = "form";
  isCheckingOrder = false;
  isSubmittingOrder = false;
  orderError = "";
  orderCheckAmounts: { [key: string]: number } | null = null;
  orderCheckError = "";
  orderResult: { success: boolean; description: string } | null = null;

  private liveCheckSubject = new Subject<void>();
  private liveCheckSub: Subscription;
  private pollInterval: any;
  private _orderTypesToken = 0;
  private _allowedOrderTypesToken = 0;

  constructor(
    private _localize: LocalizeService,
    private _element: ElementRef,
    private _authService: AuthService,
    private _insaService: InsaService,
    private _service: ControlTableService,
  ) {}

  ngOnInit(): void {
    this.liveCheckSub = this.liveCheckSubject
      .pipe(debounceTime(700))
      .subscribe(() => this.runLiveCheck());

    this._service.checkCashBalanceForUser().subscribe((check) => {
      this.checkCashBalance = check !== false;
    });

    if (this.security) this.initForSecurity();
    this.reapplyLocalize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.security && !changes.security.firstChange && this.security) {
      this.initForSecurity();
    }
  }

  ngOnDestroy(): void {
    if (this.liveCheckSub) this.liveCheckSub.unsubscribe();
    if (this.pollInterval) clearInterval(this.pollInterval);
    this.destroyGtdDatePicker();
  }

  private initForSecurity(): void {
    const sec = this.security;
    if (!sec) return;
    this.isDomestic = OrderLogic.isDomesticStockExchange(sec.SecStockExchange);
    this.resetForm();
    this.loadAllowedOrderTypes(sec.SecStockExchange);
    this.loadPositionQuantity();
    this.loadSecReservationPercent();
    this.startPricePolling();
    this.enrichSecurity();
  }

  // TraderWatchlistSecurities ne vraća valutu/FxRate/SecFactor, a bez njih
  // izračuni (FX konverzija u valutu likvidnosti) ne bi bili isti kao na
  // traderu - dohvat pune hartije iz TraderSecurities po SecNumber.
  private enrichSecurity(): void {
    const sec = this.security;
    if (!sec) return;
    if (sec.SecCurrSymbol) {
      this.loadCashBalance();
      return;
    }
    this._service
      .searchSecurities(sec.SecNumber, this.currency)
      .subscribe((list) => {
        if (this.security !== sec) return;
        const match = (list ?? []).find((s) => s.SecIndex === sec.SecIndex);
        if (match) {
          sec.SecCurrSymbol = match.SecCurrSymbol;
          sec.FxRate = match.FxRate;
          sec.SecFactor = match.SecFactor;
          sec.SecContractSize = match.SecContractSize;
          this.recomputeTotal();
        }
        this.loadCashBalance();
      });
  }

  private resetForm(): void {
    this.allowedTransactionTypes = [];
    this.allowedOrderTypes = [];
    this.tradeAmount = 0;
    this.tradePrice = 0;
    this.tradeStopPrice = 0;
    this.tradeTotal = 0;
    this.currentPrice = 0;
    this.secReservationPercent = 1;
    this.liveCheckAmounts = null;
    this.liveCheckLoading = false;
    this.orderError = "";
    this.selectedValidity = "Day";
    this.goodTillDate = "";
    this.resetConfirm();
    this.step = "form";
  }

  private resetConfirm(): void {
    this.orderResult = null;
    this.orderCheckAmounts = null;
    this.orderCheckError = "";
    this.isCheckingOrder = false;
  }

  // ---- učitavanje (isti endpointi kao trader) ----

  private loadAllowedOrderTypes(secStockExchange: number): void {
    const token = ++this._orderTypesToken;
    this._service.allowedTransactionTypes(secStockExchange).subscribe((types) => {
      if (token !== this._orderTypesToken) return;
      this.allowedTransactionTypes = [...new Set((types ?? []).map((t) => +t))];
      const first = this.allowedTransactionTypes[0];
      if (first !== undefined) this.onOrderTypeChange(first);
    });
  }

  private loadAllowedOrderTypesForTransaction(transactionType: number): void {
    if (!this.security) return;
    const token = ++this._allowedOrderTypesToken;
    this._service
      .allowedOrderTypes(this.security.SecStockExchange, transactionType)
      .subscribe((types) => {
        if (token !== this._allowedOrderTypesToken) return;
        this.allowedOrderTypes = (types ?? []).map((t) => +t);
      });
  }

  private loadCashBalance(): void {
    const sec = this.security;
    if (!sec) return;
    this.cashBalanceLoading = true;
    this.availableLiquidity = 0;
    this._service.cashBalanceForSecurity(sec, this.currency).subscribe((balance) => {
      if (this.security !== sec) return;
      this.cashBalanceLoading = false;
      this.availableLiquidity = balance ?? 0;
    });
  }

  private loadPositionQuantity(): void {
    const sec = this.security;
    if (!sec) return;
    this.heldQuantityLoading = true;
    this.heldQuantity = 0;
    this._service.positionQuantityForSecurity(sec).subscribe((qty) => {
      if (this.security !== sec) return;
      this.heldQuantityLoading = false;
      this.heldQuantity = qty ?? 0;
    });
  }

  private loadSecReservationPercent(): void {
    if (!this.security) return;
    this._service
      .secReservationPercent(this.security.SecIndex)
      .subscribe((percent) => {
        this.secReservationPercent =
          percent != null && percent > 0 ? percent : 1;
      });
  }

  private startPricePolling(): void {
    if (this.pollInterval) clearInterval(this.pollInterval);
    this.loadPrice();
    this.pollInterval = setInterval(() => this.loadPrice(), 5000);
  }

  private loadPrice(): void {
    const sec = this.security;
    if (!sec) return;
    this._service.realTimeL1(sec).subscribe((data) => {
      if (this.security !== sec || !data || data.length === 0) return;
      // Kao trader: uvijek 'price' s backenda, nikad (bid+ask)/2.
      const livePrice = data[0].price;
      if (livePrice && livePrice > 0) {
        this.currentPrice = livePrice;
        if (this.isImmediateOrder) {
          this.tradePrice = Math.round(this.currentPrice * 100) / 100;
        }
      }
    });
  }

  // ---- stanje / provjere (zajednička logika) ----

  private get orderState(): OrderLogic.OrderFormState {
    return {
      security: this.security,
      orderType: this.selectedOrderType,
      validity: this.selectedValidity,
      goodTillDate: this.goodTillDate,
      amount: this.tradeAmount,
      price: this.tradePrice,
      stopPrice: this.tradeStopPrice,
      currentPrice: this.currentPrice,
      tradeTotal: this.tradeTotal,
      availableLiquidity: this.availableLiquidity,
      heldQuantity: this.heldQuantity,
      checkCashBalance: this.checkCashBalance,
      allowedOrderTypes: this.allowedOrderTypes,
      isSubmitting: this.isSubmittingOrder,
      secReservationPercent: this.secReservationPercent,
    };
  }

  get isBuy(): boolean {
    return this.side === "Buy";
  }

  get symbol(): string {
    if (!this.security) return "";
    return (
      this.security.SecSymbol?.trim() || this.security.SecNumber?.trim() || ""
    );
  }

  get securityName(): string {
    return this.security?.SecName?.trim() || this.symbol;
  }

  get securityCurrency(): string {
    return (this.security && this.security.SecCurrSymbol) || this.currency;
  }

  get liquidityCurrency(): string {
    return this.currency;
  }

  get orderTypeItems(): { id: number; label: string }[] {
    return OrderLogic.orderTypeItems(this.allowedTransactionTypes);
  }

  get selectedOrderTypeLabel(): string {
    return OrderLogic.TRANSACTION_TYPE_LABELS[this.selectedOrderType] ?? "";
  }

  get isImmediateOrder(): boolean {
    return OrderLogic.isImmediateOrderType(this.selectedOrderType);
  }

  get validityOptions(): { id: string; label: string }[] {
    return OrderLogic.validityOptions(
      this._localize,
      this.selectedOrderType,
      this.isDomestic,
    );
  }

  get selectedValidityLabel(): string {
    return OrderLogic.validityLabel(
      this._localize,
      this.selectedOrderType,
      this.isDomestic,
      this.selectedValidity,
    );
  }

  get showStopPriceField(): boolean {
    return OrderLogic.showStopPriceField(this.selectedOrderType, this.isDomestic);
  }

  get showDatePicker(): boolean {
    return OrderLogic.showDatePicker(
      this.selectedOrderType,
      this.selectedValidity,
    );
  }

  get priceLabelForCurrentType(): string {
    return OrderLogic.priceLabel(this._localize, this.selectedOrderType);
  }

  get priceTimesQuantity(): number {
    return OrderLogic.computeTradeTotal(
      OrderLogic.effectivePrice(this.orderState),
      this.tradeAmount,
      this.security,
    );
  }

  // Strana je fiksna, pa se prikazuju samo upozorenja za tu stranu.
  get exceedsLiquidity(): boolean {
    return this.isBuy && OrderLogic.exceedsLiquidity(this.orderState);
  }

  get noFunds(): boolean {
    return this.isBuy && OrderLogic.noFunds(this.orderState);
  }

  get marketOnlyForSell(): boolean {
    return this.isBuy && OrderLogic.marketOnlyForSell(this.orderState);
  }

  get noHoldings(): boolean {
    return !this.isBuy && OrderLogic.noHoldings(this.orderState);
  }

  get exceedsSellAmount(): boolean {
    return !this.isBuy && OrderLogic.exceedsSellAmount(this.orderState);
  }

  get canSubmit(): boolean {
    return OrderLogic.canSubmitSide(this.orderState, this.side);
  }

  get liveFeeRows(): { key: string; value: number }[] {
    return OrderLogic.feeRows(this._localize, this.liveCheckAmounts);
  }

  get liveCheckNet(): number | null {
    return OrderLogic.checkNet(this.liveCheckAmounts);
  }

  get liveCheckTotal(): number | null {
    return OrderLogic.checkTotal(this.liveCheckAmounts, this.security);
  }

  get orderCheckFeeRows(): { key: string; value: number }[] {
    return OrderLogic.feeRows(this._localize, this.orderCheckAmounts);
  }

  get orderCheckNet(): number | null {
    return OrderLogic.checkNet(this.orderCheckAmounts);
  }

  get orderCheckTotal(): number | null {
    return OrderLogic.checkTotal(this.orderCheckAmounts, this.security);
  }

  get processingText(): string {
    return OrderLogic.processingText(this._localize);
  }

  get orderResultMessage(): string {
    return this.clarify(
      OrderLogic.orderResultMessage(this._localize, this.orderResult),
    );
  }

  trackById(_index: number, item: { id: number | string }): number | string {
    return item.id;
  }

  formatDateShort(value: string | Date | null | undefined): string {
    return OrderLogic.formatDateShort(value);
  }

  formatN(value: number | null | undefined, decimals = 2): string {
    if (value === null || value === undefined || isNaN(value as number))
      return "";
    return (kendo as any).toString(value, "n" + decimals);
  }

  // ---- unos (isto kao trader) ----

  onOrderTypeChange(type: number): void {
    this.selectedOrderType = +type;
    this.selectedValidity = this.isImmediateOrder ? "Immediate" : "Day";
    if (!this.showDatePicker) this.goodTillDate = "";
    this.reapplyLocalize();
    this.allowedOrderTypes = [];
    this.loadAllowedOrderTypesForTransaction(this.selectedOrderType);
    if (this.isImmediateOrder && this.currentPrice > 0) {
      this.tradePrice = Math.round(this.currentPrice * 100) / 100;
    }
    this.liveCheckAmounts = null;
    this.liveCheckLoading = false;
    const price = OrderLogic.effectivePrice(this.orderState);
    this.tradeTotal =
      price > 0
        ? OrderLogic.computeTradeTotal(price, this.tradeAmount, this.security)
        : 0;
    this.scheduleLiveCheck();
  }

  onValidityChange(v: string): void {
    this.selectedValidity = v;
    if (!this.showDatePicker) {
      this.goodTillDate = "";
      this.destroyGtdDatePicker();
    } else {
      setTimeout(() => this.initGtdDatePicker(), 0);
    }
  }

  blockNonIntegerKeys(event: KeyboardEvent): void {
    if ([".", ",", "e", "E", "+", "-"].includes(event.key)) {
      event.preventDefault();
    }
  }

  onAmountChange(v: number): void {
    this.tradeAmount = Math.max(0, Math.floor(+v || 0));
    this.recomputeTotal();
    this.scheduleLiveCheck();
  }

  onPriceChange(v: number): void {
    this.tradePrice = +v || 0;
    this.recomputeTotal();
    this.scheduleLiveCheck();
  }

  onStopPriceChange(v: number): void {
    this.tradeStopPrice = +v || 0;
  }

  private recomputeTotal(): void {
    this.tradeTotal = OrderLogic.computeTradeTotal(
      OrderLogic.effectivePrice(this.orderState),
      this.tradeAmount,
      this.security,
    );
  }

  private scheduleLiveCheck(): void {
    this.liveCheckAmounts = null;
    if (this.tradeAmount > 0 && this.security) {
      this.liveCheckLoading = true;
      this.liveCheckSubject.next();
    } else {
      this.liveCheckLoading = false;
    }
  }

  private buildOrder(): any {
    return OrderLogic.buildTraderOrder(
      this.orderState,
      this.side,
      this._authService.UserInfo?.PartnerIndex ?? 0,
    );
  }

  private runLiveCheck(): void {
    if (!this.security || this.tradeAmount <= 0) {
      this.liveCheckLoading = false;
      return;
    }
    this._service.orderCheck(this.buildOrder()).subscribe({
      next: (res) => {
        this.liveCheckLoading = false;
        this.liveCheckAmounts =
          res && res.amounts && Object.keys(res.amounts).length > 0
            ? res.amounts
            : null;
        this.reapplyLocalize();
      },
      error: () => {
        this.liveCheckLoading = false;
        this.liveCheckAmounts = null;
      },
    });
  }

  // GTD datum - isti Kendo DatePicker (dd.MM.yyyy, bez prošlih datuma) kao trader.
  private initGtdDatePicker(): void {
    const $el = $(`#${this.gtdInputId}`) as any;
    if (!$el.length) return;
    if ($el.data("kendoDatePicker")) $el.data("kendoDatePicker").destroy();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    $el.kendoDatePicker({
      format: "dd.MM.yyyy",
      min: today,
      value: this.goodTillDate ? new Date(this.goodTillDate) : null,
      culture: this._localize.getLocale(),
      month: {
        empty: '<a class="k-link k-past-date-disabled">#= data.value #</a>',
      },
      change: (evt: any) => {
        const d: Date = evt.sender.value();
        if (d) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          this.goodTillDate = `${y}-${m}-${day}`;
        } else {
          this.goodTillDate = "";
        }
      },
    });
  }

  private destroyGtdDatePicker(): void {
    const $el = $(`#${this.gtdInputId}`) as any;
    if ($el.length && $el.data("kendoDatePicker"))
      $el.data("kendoDatePicker").destroy();
  }

  // ---- potvrda i unos naloga (isto kao confirm modal na traderu) ----

  openOrderConfirm(): void {
    if (!this.canSubmit) return;
    this.destroyGtdDatePicker();
    this.resetConfirm();
    this.orderError = "";
    this.step = "confirm";
    this.isCheckingOrder = true;
    this.reapplyLocalize();
    this._authService.getUserInfo().subscribe({
      next: () => this.runOrderCheck(),
      error: () => {
        this.isCheckingOrder = false;
      },
    });
  }

  private runOrderCheck(): void {
    this.isCheckingOrder = true;
    this._service.orderCheck(this.buildOrder()).subscribe({
      next: (res) => {
        this.isCheckingOrder = false;
        if (res && res.success) {
          this.orderCheckAmounts = res.amounts;
        } else {
          this.orderCheckError = this.clarify(
            res && res.description
              ? res.description
              : this._localize.getLocalizedResource("Res1144") ||
                  "Order check failed.",
          );
        }
        this.reapplyLocalize();
      },
      error: (err) => {
        this.isCheckingOrder = false;
        this.orderCheckError =
          err?.message ??
          (this._localize.getLocalizedResource("Res1145") ||
            "Error checking order.");
        this.reapplyLocalize();
      },
    });
  }

  confirmOrder(): void {
    if (!this.security || this.isSubmittingOrder || this.isCheckingOrder)
      return;
    if (!this.canSubmit) return;
    if (!this.orderCheckAmounts || !!this.orderCheckError) return;
    this.isSubmittingOrder = true;
    this.orderResult = null;
    this._service.orderInsert(this.buildOrder()).subscribe({
      next: (res) => {
        this.isSubmittingOrder = false;
        this.orderResult = {
          success: res.success,
          description: res.description,
        };
        if (res.success) {
          this.tradeAmount = 0;
          this.tradeTotal = 0;
          this.liveCheckAmounts = null;
          this.loadCashBalance();
          this.loadPositionQuantity();
          this._insaService.createReport(true);
        }
        this.reapplyLocalize();
      },
      error: (err) => {
        this.isSubmittingOrder = false;
        this.orderResult = {
          success: false,
          description:
            err?.message ??
            (this._localize.getLocalizedResource("Res1146") ||
              "Error submitting order."),
        };
        this.reapplyLocalize();
      },
    });
  }

  // "Odustani" na potvrdi vraća na unos (na traderu zatvara confirm i ostaje panel).
  backToForm(): void {
    if (this.isSubmittingOrder) return;
    this.resetConfirm();
    this.step = "form";
    this.reapplyLocalize();
    if (this.showDatePicker) setTimeout(() => this.initGtdDatePicker(), 0);
  }

  close(): void {
    if (this.isSubmittingOrder) return;
    this.closed.emit();
  }

  // Backendova poruka "Nedozvoljen iznos naloga!" klijentu nije jasna (NLB feedback).
  clarify(msg: string): string {
    if (!msg) return "";
    const m = msg.trim().toLowerCase();
    if (m === "nedozvoljen iznos naloga!")
      return (
        this._localize.getLocalizedResource("Res1638") ||
        "Iznos naloga prekoračuje raspoloživa sredstva ili dozvoljeni limit za ovu hartiju."
      );
    return msg;
  }

  private reapplyLocalize(): void {
    setTimeout(
      () => this._localize.applyTranslations(this._element.nativeElement),
      0,
    );
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-control-table/control-table-order-modal.component.ts